import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { loadOptions } from '../../../utils/selectOptions';
import { purchasePaymentUrl, buyUrl, paymentTypeUrl, checkUrl, ownCheckUrl } from '../../../constants/urls';
import Toast from '../../../components/Toast';
import BasicDatePicker from '../../../components/DatePicker';
import AmountInput from '../../../components/AmountInput';
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/date';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalanceOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

// ── Estilos ───────────────────────────────────────────────────────────────────
const inputCls = (hasError) =>
  `w-full bg-surface-low px-3 py-2.5 rounded-lg border text-sm text-on-surface placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all ${
    hasError
      ? 'border-red-400 ring-2 ring-red-100'
      : 'border-border-subtle focus:border-blue-lahuerta/40 focus:ring-blue-lahuerta/10'
  }`;

const readonlyCls =
  'w-full bg-field-locked px-3 py-2.5 rounded-lg border border-field-locked-border text-sm text-on-surface-muted select-none cursor-not-allowed';

const labelCls = 'block text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider mb-1.5';

const SectionCard = ({ icon, title, children, cols = 2 }) => (
  <section className="space-y-3">
    <div className="flex items-center gap-2 px-1">
      <span className="text-blue-lahuerta">{icon}</span>
      <h2 className="text-base font-semibold text-on-surface">{title}</h2>
    </div>
    <div className={`bg-surface-card p-6 rounded-xl shadow-sm border border-border-subtle grid grid-cols-1 md:grid-cols-${cols} gap-6`}>
      {children}
    </div>
  </section>
);

const ReadonlyField = ({ label, value, highlight = false, helpText }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center gap-1.5 mb-1.5">
      <label className="block text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider">{label}</label>
      {helpText && (
        <Tooltip title={helpText} placement="top" arrow>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: '#9ca3af', cursor: 'help', '&:hover': { color: '#4a7bc4' } }} />
        </Tooltip>
      )}
    </div>
    <div className={
      highlight
        ? 'w-full px-3 py-2.5 rounded-lg border text-sm font-semibold select-none cursor-not-allowed bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400'
        : readonlyCls
    }>
      {value || '—'}
    </div>
  </div>
);

const FieldError = ({ error, touched }) =>
  touched && error ? <p className="mt-1 text-xs text-red-500">{error}</p> : null;

const today = () => dayjs().format('YYYY-MM-DD');

// ── Componente principal ──────────────────────────────────────────────────────
const PurchasePaymentForm = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState({ open: false, message: '' });
  const [selectOptions, setSelectOptions] = useState({ buys: [], paymentTypes: [], checks: [], ownChecks: [] });
  const [selectedBuy, setSelectedBuy] = useState(null);

  const initialValues = {
    buy: '',
    paymentDate: today(),
    amount: '',
    paymentType: '',
    chequeId: '',
    ownCheckId: '',
  };

  useEffect(() => {
    const load = async () => {
      const [buys, paymentTypes, checks] = await Promise.all([
        loadOptions(buyUrl, (data) =>
          data
            .filter((b) => b.payment_status !== 'ABONADO')
            .map((b) => ({
              name: `${String(b.id).padStart(8, '0')} — ${b.proveedor?.nombre || b.proveedor} — ${formatDate(b.fecha)}`,
              value: b.id,
            }))
        ),
        loadOptions(paymentTypeUrl, (data) =>
          data.map((t) => ({ name: t.descripcion, value: t.id }))
        ),
        loadOptions(`${checkUrl}?estado=EN_CARTERA`, (data) =>
          data.map((c) => ({
            name: `Nro. ${c.numero} — ${c.banco?.descripcion || c.banco} — ${formatCurrency(c.importe)}`,
            value: c.id,
            importe: c.importe,
          }))
        ),
      ]);
      setSelectOptions((prev) => ({ ...prev, buys, paymentTypes, checks, ownChecks: [] }));
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBuyChange = async (buyId, setFieldValue) => {
    setFieldValue('buy', buyId);
    setFieldValue('amount', '');
    setFieldValue('paymentType', '');
    setFieldValue('chequeId', '');
    setFieldValue('ownCheckId', '');

    if (!buyId) {
      setSelectedBuy(null);
      setSelectOptions((prev) => ({ ...prev, ownChecks: [] }));
      return;
    }
    try {
      const { data } = await axios.get(`${buyUrl}${buyId}/`);
      setSelectedBuy(data);

      const proveedorId = data.proveedor?.id;
      if (proveedorId) {
        const ownChecks = await loadOptions(
          `${ownCheckUrl}?available=true&supplier_id=${proveedorId}`,
          (items) => items.map((c) => ({
            name: `Nro. ${c.numero} — ${c.banco?.descripcion || c.banco} — Saldo: ${formatCurrency(c.remaining_balance)}`,
            value: c.id,
            importe: c.remaining_balance,
          }))
        );
        setSelectOptions((prev) => ({ ...prev, ownChecks }));
      }
    } catch {
      setSelectedBuy(null);
      setSelectOptions((prev) => ({ ...prev, ownChecks: [] }));
    }
  };

  const validationSchema = Yup.object({
    buy:         Yup.string().required('Requerido'),
    paymentDate: Yup.date().nullable().required('Requerido'),
    amount:      Yup.number()
      .required('Requerido')
      .positive('El importe debe ser mayor a cero'),
    paymentType: Yup.string().required('Requerido'),
  });

  const validate = (values) => {
    const errors = {};
    const selected = selectOptions.paymentTypes.find(
      (opt) => String(opt.value) === String(values.paymentType)
    );
    if (selected?.name === 'Cheque' && !values.chequeId) {
      errors.chequeId = 'Requerido cuando el tipo de pago es cheque.';
    }
    if (selected?.name === 'Cheque Propio' && !values.ownCheckId) {
      errors.ownCheckId = 'Requerido cuando el tipo de pago es cheque propio.';
    }
    return errors;
  };

  const handleSubmit = async (values, { setFieldError }) => {
    const selected = selectOptions.paymentTypes.find(
      (opt) => String(opt.value) === String(values.paymentType)
    );
    const payload = {
      compra:          values.buy,
      importe_abonado: values.amount,
      fecha_pago:      values.paymentDate,
      tipo_pago:       values.paymentType,
    };
    if (selected?.name === 'Cheque') {
      payload.cheque_id = values.chequeId;
    }
    if (selected?.name === 'Cheque Propio') {
      payload.own_check_id = values.ownCheckId;
    }
    const formatDetailAmounts = (msg) =>
      msg.replace(/\((\d+(?:\.\d+)?)\)/g, (_, num) => `(${formatCurrency(parseFloat(num))})`);

    try {
      await axios.post(purchasePaymentUrl, payload);
      navigate('/purchase-payment');
    } catch (error) {
      const detail = error?.response?.data?.detail || '';
      if (detail.toLowerCase().includes('saldo pendiente')) {
        setFieldError('amount', 'El importe supera el saldo pendiente de la compra.');
      } else {
        const msg = detail
          ? formatDetailAmounts(detail)
          : error?.response?.data?.cheque_id?.[0] || 'Error al registrar el pago.';
        setToast({ open: true, message: msg });
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validate={validate}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, setFieldValue }) => {
        const selectedPaymentType = selectOptions.paymentTypes.find(
          (opt) => String(opt.value) === String(values.paymentType)
        );
        const isCheckPayment = selectedPaymentType?.name === 'Cheque';
        const isOwnCheckPayment = selectedPaymentType?.name === 'Cheque Propio';

        return (
          <Form className="w-full max-w-5xl mx-auto space-y-8 pb-12">
            <Toast
              open={toast.open}
              message={toast.message}
              onClose={() => setToast({ open: false, message: '' })}
            />

            {/* Breadcrumbs */}
            <nav className="flex items-center flex-wrap gap-2 text-sm font-medium text-on-surface-muted">
              <span className="whitespace-nowrap hover:text-accent cursor-pointer transition-colors" onClick={() => navigate('/')}>Inicio</span>
              <span className="text-xs">›</span>
              <span className="whitespace-nowrap hover:text-accent cursor-pointer transition-colors" onClick={() => navigate('/purchase-payment')}>Pagos de Compras</span>
              <span className="text-xs">›</span>
              <span className="text-on-surface font-semibold">Nuevo</span>
            </nav>

            {/* 1. Selección de compra */}
            <SectionCard icon={<ShoppingCartIcon sx={{ fontSize: 20 }} />} title="Compra" cols={1}>
              <div className="flex flex-col gap-1">
                <label className={labelCls}>Seleccionar compra</label>
                <select
                  value={values.buy}
                  onChange={(e) => handleBuyChange(e.target.value, setFieldValue)}
                  className={inputCls(touched.buy && errors.buy)}
                >
                  <option value="">Seleccionar...</option>
                  {selectOptions.buys.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.name}</option>
                  ))}
                </select>
                <FieldError error={errors.buy} touched={touched.buy} />
              </div>
            </SectionCard>

            {/* 2. Datos de la compra (readonly, visible al seleccionar) */}
            {selectedBuy && (
              <SectionCard icon={<ReceiptLongIcon sx={{ fontSize: 20 }} />} title="Datos de la compra" cols={2}>
                <ReadonlyField label="N° Compra"      value={String(selectedBuy.id).padStart(8, '0')} />
                <ReadonlyField label="Proveedor"      value={selectedBuy.proveedor?.nombre} />
                <ReadonlyField label="Fecha"          value={formatDate(selectedBuy.fecha)} />
                <ReadonlyField
                  label="Importe total"
                  value={formatCurrency(selectedBuy.importe)}
                  helpText="Importe original de la compra, sin tener en cuenta los pagos ya realizados."
                />
                <ReadonlyField
                  label="Saldo pendiente"
                  value={formatCurrency(selectedBuy.outstanding_balance)}
                  highlight
                  helpText="Monto que aún resta abonar. Es el importe total menos los pagos ya registrados para esta compra."
                />
                <ReadonlyField label="Estado"         value={selectedBuy.payment_status} />
              </SectionCard>
            )}

            {/* 3. Datos del pago (visible al seleccionar compra) */}
            {selectedBuy && (
              <SectionCard icon={<AccountBalanceIcon sx={{ fontSize: 20 }} />} title="Datos del pago" cols={2}>
                <div className="flex flex-col gap-1">
                  <label className={labelCls}>Fecha de pago</label>
                  <BasicDatePicker
                    value={values.paymentDate}
                    onChange={(date) => setFieldValue('paymentDate', date)}
                    hasError={touched.paymentDate && Boolean(errors.paymentDate)}
                    error={touched.paymentDate && errors.paymentDate}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className={labelCls}>Tipo de pago</label>
                  <select
                    value={values.paymentType}
                    onChange={(e) => {
                      setFieldValue('paymentType', e.target.value);
                      setFieldValue('chequeId', '');
                      setFieldValue('ownCheckId', '');
                      setFieldValue('amount', '');
                    }}
                    className={inputCls(touched.paymentType && errors.paymentType)}
                  >
                    <option value="">Seleccionar...</option>
                    {selectOptions.paymentTypes.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.name}</option>
                    ))}
                  </select>
                  <FieldError error={errors.paymentType} touched={touched.paymentType} />
                </div>

                {values.paymentType && !isCheckPayment && !isOwnCheckPayment && (
                  <div className="flex flex-col gap-1">
                    <label className={labelCls}>Importe abonado</label>
                    <AmountInput
                      name="amount"
                      value={values.amount}
                      onChange={(raw) => setFieldValue('amount', raw)}
                      hasError={touched.amount && Boolean(errors.amount)}
                    />
                    <FieldError error={errors.amount} touched={touched.amount} />
                  </div>
                )}
              </SectionCard>
            )}

            {/* 4. Cheque (condicional) */}
            {isCheckPayment && (
              <SectionCard icon={<AccountBalanceIcon sx={{ fontSize: 20 }} />} title="Cheque" cols={1}>
                <div className="flex flex-col gap-1">
                  <label className={labelCls}>Cheque en cartera</label>
                  <select
                    value={values.chequeId}
                    onChange={(e) => {
                      const selected = selectOptions.checks.find((c) => String(c.value) === e.target.value);
                      setFieldValue('chequeId', e.target.value);
                      setFieldValue('amount', selected ? String(selected.importe) : '');
                    }}
                    className={inputCls(touched.chequeId && errors.chequeId)}
                  >
                    <option value="">Seleccionar cheque...</option>
                    {selectOptions.checks.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.name}</option>
                    ))}
                  </select>
                  <FieldError error={errors.chequeId} touched={touched.chequeId} />
                  {selectOptions.checks.length === 0 && (
                    <p className="mt-1 text-xs text-on-surface-muted">No hay cheques en cartera disponibles.</p>
                  )}
                </div>

                {values.chequeId && (
                  <div className="flex flex-col gap-1">
                    <label className={labelCls}>Importe abonado</label>
                    <AmountInput
                      name="amount"
                      value={values.amount}
                      onChange={(raw) => setFieldValue('amount', raw)}
                      hasError={touched.amount && Boolean(errors.amount)}
                    />
                    <FieldError error={errors.amount} touched={touched.amount} />
                  </div>
                )}
              </SectionCard>
            )}

            {/* 5. Cheque Propio (condicional) */}
            {isOwnCheckPayment && (
              <SectionCard icon={<AccountBalanceIcon sx={{ fontSize: 20 }} />} title="Cheque Propio" cols={1}>
                <div className="flex flex-col gap-1">
                  <label className={labelCls}>Cheque propio disponible</label>
                  <select
                    value={values.ownCheckId}
                    onChange={(e) => {
                      const selected = selectOptions.ownChecks.find((c) => String(c.value) === e.target.value);
                      setFieldValue('ownCheckId', e.target.value);
                      setFieldValue('amount', selected ? String(selected.importe) : '');
                    }}
                    className={inputCls(touched.ownCheckId && errors.ownCheckId)}
                  >
                    <option value="">Seleccionar cheque...</option>
                    {selectOptions.ownChecks.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.name}</option>
                    ))}
                  </select>
                  <FieldError error={errors.ownCheckId} touched={touched.ownCheckId} />
                  {selectOptions.ownChecks.length === 0 && (
                    <p className="mt-1 text-xs text-on-surface-muted">No hay cheques propios disponibles.</p>
                  )}
                </div>

                {values.ownCheckId && (
                  <div className="flex flex-col gap-1">
                    <label className={labelCls}>Importe abonado</label>
                    <AmountInput
                      name="amount"
                      value={values.amount}
                      onChange={(raw) => setFieldValue('amount', raw)}
                      hasError={touched.amount && Boolean(errors.amount)}
                    />
                    <FieldError error={errors.amount} touched={touched.amount} />
                  </div>
                )}
              </SectionCard>
            )}

            {/* Action Bar */}
            <div className="flex items-center justify-end gap-4 pt-6 border-t border-border-subtle">
              <button
                type="button"
                onClick={() => navigate('/purchase-payment')}
                className="px-6 py-2.5 text-sm font-semibold text-on-surface-muted border border-border-subtle rounded-lg hover:border-red-400 hover:text-red-500 hover:bg-red-50 hover:font-bold transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!selectedBuy}
                className="px-8 py-2.5 bg-blue-lahuerta text-white font-bold text-sm rounded-lg shadow-sm hover:bg-blue-lahuerta/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Registrar Pago
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PurchasePaymentForm;
